/**
 * Base client for Alpaca API communication
 * Handles authentication and request/response processing
 */

import axios from 'axios';
import { getConfig, getBaseUrls, isConfigValid } from './config';
import { executeWithRetry, parseAlpacaError } from './errors';

// Create separate clients for trading and data APIs
const tradeApiClient = axios.create({});
const dataApiClient = axios.create({});

/**
 * Add authentication headers to a request
 */
function addAuthHeaders(config: any): any {
  const appConfig = getConfig(); // Get config from the module
  const keyId = appConfig.keyId;
  const secretKey = appConfig.secretKey;
  console.log(`Using Alpaca API Key (${appConfig.paper ? 'PAPER' : 'LIVE'}): ${keyId ? keyId.substring(0, 4) + '...' + keyId.substring(keyId.length - 4) : 'NOT SET'}`);
  return {
    ...config,
    headers: {
      ...config.headers,
      'APCA-API-KEY-ID': keyId,
      'APCA-API-SECRET-KEY': secretKey,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  };
}

/**
 * Make a request to the Alpaca Trading API
 */
export async function makeTradeRequest<T>(
  method: string,
  endpoint: string,
  data?: any,
  options: any = {}
): Promise<T> {
  const appConfig = getConfig(); // Get config from the module
  const baseUrl = appConfig.baseUrl;
  const url = `${baseUrl}/v2${endpoint}`;
  console.log(`Making Alpaca trade request to: ${url}`);
  return executeWithRetry(async () => {
    try {
      const response: any = await tradeApiClient({
        method,
        url,
        data,
        ...addAuthHeaders(options),
      });
      return response.data;
    } catch (error) {
      throw parseAlpacaError(error);
    }
  });
}

/**
 * Make a request to the Alpaca Data API
 */
export async function makeDataRequest<T>(
  method: string,
  endpoint: string,
  params?: any,
  options: any = {}
): Promise<T> {
  const appConfig = getConfig(); // Get config from the module
  const dataBaseUrl = appConfig.dataBaseUrl;
  const url = `${dataBaseUrl}/v2${endpoint}`;
  console.log(`Making Alpaca data request to: ${url}`);
  return executeWithRetry(async () => {
    try {
      const response: any = await dataApiClient({
        method,
        url,
        params,
        ...addAuthHeaders(options),
      });
      return response.data;
    } catch (error) {
      throw parseAlpacaError(error);
    }
  });
}